import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ProjectListItem, ProjectStatus } from "@/mocks/admin/projects";

type Props = {
  open: boolean;
  onClose: () => void;
  projects: ProjectListItem[];
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DAILY_CAPACITY = 4;

// Parse strings like "Jun 01, 2026" or "May 31, 2026"
function parseProjectDate(str: string): Date | null {
  if (!str) return null;
  const match = str.match(/([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})/);
  if (!match) return null;
  const monthName = match[1];
  const day = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);
  const monthIdx = MONTHS.findIndex((m) =>
    m.toLowerCase().startsWith(monthName.toLowerCase()),
  );
  if (monthIdx < 0) return null;
  return new Date(year, monthIdx, day);
}

const statusStyles: Record<
  ProjectStatus,
  { dot: string; bg: string; text: string; label: string }
> = {
  scheduled: {
    dot: "bg-indigo-500",
    bg: "bg-indigo-50",
    text: "text-indigo-700",
    label: "Scheduled",
  },
  "in-progress": {
    dot: "bg-amber-500",
    bg: "bg-amber-50",
    text: "text-amber-700",
    label: "In Progress",
  },
  completed: {
    dot: "bg-emerald-500",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    label: "Completed",
  },
  "on-hold": {
    dot: "bg-rose-500",
    bg: "bg-rose-50",
    text: "text-rose-700",
    label: "On Hold",
  },
  cancelled: {
    dot: "bg-ink-400",
    bg: "bg-cream-100",
    text: "text-ink-600",
    label: "Cancelled",
  },
};

const STATUS_FILTERS: { key: ProjectStatus | "all"; label: string }[] = [
  { key: "all", label: "All" },
  { key: "scheduled", label: "Scheduled" },
  { key: "in-progress", label: "In Progress" },
  { key: "completed", label: "Completed" },
  { key: "on-hold", label: "On Hold" },
];

export default function ProjectsCalendarModal({ open, onClose, projects }: Props) {
  const today = new Date();
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | "all">("all");

  useEffect(() => {
    if (open) {
      // Default to the month containing most projects (current month)
      setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
      setSelectedDay(null);
      setStatusFilter("all");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const visibleProjects = useMemo(() => {
    if (statusFilter === "all") return projects;
    return projects.filter((p) => p.status === statusFilter);
  }, [projects, statusFilter]);

  // Group by yyyy-mm-dd
  const projectsByDate = useMemo(() => {
    const map = new Map<string, ProjectListItem[]>();
    visibleProjects.forEach((p) => {
      const d = parseProjectDate(p.date);
      if (!d) return;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    });
    // Sort each day's items by time
    map.forEach((arr) => arr.sort((a, b) => a.time.localeCompare(b.time)));
    return map;
  }, [visibleProjects]);

  // Build calendar grid
  const grid = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: { date: Date | null; key: string }[] = [];
    for (let i = 0; i < firstDay; i++) cells.push({ date: null, key: `pad-${i}` });
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      cells.push({ date, key: `${year}-${month}-${d}` });
    }
    while (cells.length % 7 !== 0) cells.push({ date: null, key: `end-${cells.length}` });
    return cells;
  }, [cursor]);

  const monthProjects = useMemo(() => {
    const arr: ProjectListItem[] = [];
    projectsByDate.forEach((items, key) => {
      const [y, m] = key.split("-").map(Number);
      if (y === cursor.getFullYear() && m === cursor.getMonth()) arr.push(...items);
    });
    return arr;
  }, [projectsByDate, cursor]);

  const monthRevenue = monthProjects.reduce((s, p) => s + (p.total || 0), 0);

  const busiestDay = useMemo(() => {
    let max = 0;
    let dayKey = "";
    projectsByDate.forEach((arr, key) => {
      const [y, m] = key.split("-").map(Number);
      if (y !== cursor.getFullYear() || m !== cursor.getMonth()) return;
      if (arr.length > max) {
        max = arr.length;
        dayKey = key;
      }
    });
    if (!dayKey) return null;
    const [y, m, d] = dayKey.split("-").map(Number);
    return { date: new Date(y, m, d), count: max };
  }, [projectsByDate, cursor]);

  const openDays = grid.filter((c) => {
    if (!c.date) return false;
    return (projectsByDate.get(c.key) || []).length === 0;
  }).length;

  if (!open) return null;

  const selectedItems = selectedDay ? projectsByDate.get(selectedDay) || [] : [];

  const handleOpenProject = (id: string) => {
    onClose();
    navigate(`/projects/${id}`);
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-ink-900/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white w-full max-w-6xl rounded-lg border border-ink-100 max-h-[92vh] flex flex-col pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-brand-100 text-brand-700">
                <i className="ri-calendar-2-line text-lg"></i>
              </span>
              <div>
                <h2 className="font-display text-lg font-bold text-ink-900">
                  Projects Calendar
                </h2>
                <div className="text-xs text-ink-500">
                  Every move on the books — see your week, plan ahead, spot the busy days.
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-cream-100 text-ink-500 cursor-pointer"
              aria-label="Close"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Stats strip */}
          <div className="px-6 py-4 border-b border-ink-100 bg-cream-50 grid grid-cols-2 md:grid-cols-4 gap-3 shrink-0">
            <StatTile
              icon="ri-stack-line"
              iconClass="text-indigo-700 bg-indigo-50"
              label="Projects This Month"
              value={String(monthProjects.length)}
              sub={`${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`}
            />
            <StatTile
              icon="ri-money-dollar-circle-line"
              iconClass="text-emerald-700 bg-emerald-50"
              label="Booked Revenue"
              value={`$${monthRevenue.toLocaleString()}`}
              sub="this month"
            />
            <StatTile
              icon="ri-fire-line"
              iconClass="text-rose-700 bg-rose-50"
              label="Busiest Day"
              value={
                busiestDay
                  ? `${MONTHS[busiestDay.date.getMonth()].slice(0, 3)} ${busiestDay.date.getDate()}`
                  : "—"
              }
              sub={busiestDay ? `${busiestDay.count} jobs` : "no data"}
            />
            <StatTile
              icon="ri-leaf-line"
              iconClass="text-brand-700 bg-brand-50"
              label="Open Days"
              value={String(openDays)}
              sub="this month"
            />
          </div>

          {/* Filter chips */}
          <div className="px-6 py-3 border-b border-ink-100 flex items-center gap-2 flex-wrap shrink-0">
            <span className="text-[10px] font-bold uppercase tracking-wider text-ink-500 mr-1">
              Show
            </span>
            {STATUS_FILTERS.map((f) => {
              const active = statusFilter === f.key;
              const style = f.key !== "all" ? statusStyles[f.key as ProjectStatus] : null;
              return (
                <button
                  key={f.key}
                  onClick={() => setStatusFilter(f.key)}
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full border cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5 transition-colors ${
                    active
                      ? "bg-ink-900 border-ink-900 text-white"
                      : "bg-white border-ink-200 text-ink-700 hover:bg-cream-100"
                  }`}
                >
                  {style && (
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${style.dot} ${
                        active ? "opacity-90" : ""
                      }`}
                    ></span>
                  )}
                  {f.label}
                </button>
              );
            })}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto scrollbar-thin">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
              {/* Calendar */}
              <div className="lg:col-span-2 p-6 border-b lg:border-b-0 lg:border-r border-ink-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-lg font-bold text-ink-900">
                    {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
                  </h3>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() =>
                        setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))
                      }
                      className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-cream-100 text-ink-500 cursor-pointer"
                      aria-label="Previous month"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <button
                      onClick={() =>
                        setCursor(new Date(today.getFullYear(), today.getMonth(), 1))
                      }
                      className="px-3 h-9 flex items-center rounded-md hover:bg-cream-100 text-ink-700 text-xs font-semibold cursor-pointer whitespace-nowrap"
                    >
                      Today
                    </button>
                    <button
                      onClick={() =>
                        setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))
                      }
                      className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-cream-100 text-ink-500 cursor-pointer"
                      aria-label="Next month"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                </div>

                {/* Weekday header */}
                <div className="grid grid-cols-7 gap-1.5 mb-1.5">
                  {WEEKDAYS.map((d) => (
                    <div
                      key={d}
                      className="text-[10px] font-bold uppercase tracking-wider text-ink-500 text-center py-1"
                    >
                      {d}
                    </div>
                  ))}
                </div>

                {/* Grid */}
                <div className="grid grid-cols-7 gap-1.5">
                  {grid.map((cell) => {
                    if (!cell.date) {
                      return <div key={cell.key} className="aspect-square"></div>;
                    }
                    const items = projectsByDate.get(cell.key) || [];
                    const count = items.length;
                    const isToday =
                      cell.date.toDateString() === today.toDateString();
                    const isSelected = selectedDay === cell.key;
                    const isPast =
                      cell.date <
                      new Date(today.getFullYear(), today.getMonth(), today.getDate());

                    let load: "open" | "moderate" | "busy" | "full" = "open";
                    if (count >= DAILY_CAPACITY) load = "full";
                    else if (count >= 3) load = "busy";
                    else if (count >= 1) load = "moderate";

                    const loadStyles = {
                      open: "bg-white border-ink-100 hover:border-brand-300",
                      moderate: "bg-amber-50/60 border-amber-200 hover:border-amber-400",
                      busy: "bg-rose-50/60 border-rose-200 hover:border-rose-400",
                      full: "bg-ink-900 border-ink-900 text-white",
                    }[load];

                    return (
                      <button
                        key={cell.key}
                        onClick={() => setSelectedDay(cell.key)}
                        className={`aspect-square rounded-md border p-1.5 flex flex-col items-start justify-between text-left cursor-pointer transition-all ${loadStyles} ${
                          isSelected ? "ring-2 ring-ink-900 ring-offset-1" : ""
                        } ${isPast && load === "open" ? "opacity-50" : ""}`}
                      >
                        <div className="flex items-center justify-between w-full">
                          <span
                            className={`text-xs font-bold ${
                              load === "full"
                                ? "text-white"
                                : isToday
                                ? "text-brand-600"
                                : "text-ink-900"
                            }`}
                          >
                            {cell.date.getDate()}
                          </span>
                          {isToday && (
                            <span
                              className={`text-[8px] font-bold uppercase tracking-wider ${
                                load === "full" ? "text-white" : "text-brand-600"
                              }`}
                            >
                              Today
                            </span>
                          )}
                        </div>

                        {count > 0 ? (
                          <div className="w-full flex items-end justify-between">
                            <div className="flex -space-x-0.5">
                              {items.slice(0, 3).map((p) => (
                                <span
                                  key={p.id}
                                  className={`w-1.5 h-1.5 rounded-full ${
                                    statusStyles[p.status]?.dot || "bg-ink-400"
                                  } ${load === "full" ? "ring-1 ring-ink-900" : ""}`}
                                ></span>
                              ))}
                            </div>
                            <span
                              className={`text-[10px] font-bold tabular-nums ${
                                load === "full" ? "text-white" : "text-ink-700"
                              }`}
                            >
                              {count}
                            </span>
                          </div>
                        ) : (
                          <span
                            className={`text-[9px] font-semibold uppercase tracking-wider ${
                              isPast ? "text-ink-400" : "text-emerald-700"
                            }`}
                          >
                            {isPast ? "—" : "Open"}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="mt-5 pt-4 border-t border-ink-100 flex flex-wrap items-center gap-4 text-[11px] text-ink-500">
                  <span className="font-bold uppercase tracking-wider text-ink-700">Load</span>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded bg-white border border-ink-100"></span>
                    Open
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded bg-amber-50 border border-amber-200"></span>
                    Moderate (1–2)
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded bg-rose-50 border border-rose-200"></span>
                    Busy (3)
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded bg-ink-900"></span>
                    Fully booked (4+)
                  </div>
                </div>
              </div>

              {/* Day detail */}
              <div className="p-6 bg-cream-50/60">
                {selectedDay ? (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                          Selected day
                        </div>
                        <h3 className="font-display text-base font-bold text-ink-900 mt-0.5">
                          {(() => {
                            const [y, m, d] = selectedDay.split("-").map(Number);
                            const dt = new Date(y, m, d);
                            return dt.toLocaleDateString("en-US", {
                              weekday: "long",
                              month: "long",
                              day: "numeric",
                            });
                          })()}
                        </h3>
                      </div>
                      <button
                        onClick={() => setSelectedDay(null)}
                        className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white text-ink-500 cursor-pointer"
                      >
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    </div>

                    <div className="bg-white border border-ink-100 rounded-md p-3 mb-4 flex items-center justify-between">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                          Crew Load
                        </div>
                        <div className="text-sm font-bold text-ink-900 mt-0.5">
                          {selectedItems.length} / {DAILY_CAPACITY} jobs
                        </div>
                      </div>
                      <div className="w-24 h-2 bg-cream-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            selectedItems.length >= DAILY_CAPACITY
                              ? "bg-rose-500"
                              : selectedItems.length >= 3
                              ? "bg-amber-500"
                              : "bg-emerald-500"
                          }`}
                          style={{
                            width: `${Math.min(
                              100,
                              (selectedItems.length / DAILY_CAPACITY) * 100,
                            )}%`,
                          }}
                        ></div>
                      </div>
                    </div>

                    {selectedItems.length === 0 ? (
                      <div className="text-center py-10">
                        <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-emerald-50 text-emerald-600 mb-3">
                          <i className="ri-leaf-line text-xl"></i>
                        </span>
                        <div className="font-display text-sm font-bold text-ink-900">
                          Nothing on the books
                        </div>
                        <div className="text-xs text-ink-500 mt-1">
                          No projects scheduled for this day.
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500 mb-1">
                          Projects ({selectedItems.length})
                        </div>
                        {selectedItems.map((p) => {
                          const sc = statusStyles[p.status];
                          return (
                            <button
                              key={p.id}
                              onClick={() => handleOpenProject(p.id)}
                              className="w-full text-left bg-white border border-ink-100 rounded-md p-3 hover:border-ink-300 transition-colors cursor-pointer group"
                            >
                              <div className="flex items-start gap-3">
                                <span
                                  className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-full text-white text-xs font-bold ${p.customerColor}`}
                                >
                                  {p.customerInitials}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="text-sm font-bold text-ink-900 truncate">
                                      {p.customer}
                                    </div>
                                    <span className="text-[10px] font-mono text-ink-500 shrink-0">
                                      {p.id}
                                    </span>
                                  </div>
                                  <div className="text-[11px] text-ink-500 truncate mt-0.5">
                                    {p.moveType}
                                  </div>
                                  <div className="flex items-center gap-2 mt-1.5 text-[10px]">
                                    <span className="flex items-center gap-1 text-ink-500">
                                      <i className="ri-time-line"></i>
                                      {p.time}
                                    </span>
                                    <span className="text-ink-300">•</span>
                                    <span className="flex items-center gap-1 text-ink-500 truncate">
                                      <i className="ri-route-line"></i>
                                      <span className="truncate">
                                        {p.fromCity.split(",")[0]} →{" "}
                                        {p.toCity.split(",")[0]}
                                      </span>
                                    </span>
                                  </div>
                                  <div className="mt-2 flex items-center justify-between">
                                    <span
                                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${sc.bg} ${sc.text} inline-flex items-center gap-1`}
                                    >
                                      <span
                                        className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}
                                      ></span>
                                      {sc.label}
                                    </span>
                                    <span className="flex items-center gap-2 text-[11px] text-ink-500">
                                      <span className="inline-flex items-center gap-1">
                                        <i className="ri-team-line"></i>
                                        {p.crewCount}
                                      </span>
                                      <span className="text-ink-300">•</span>
                                      {p.total > 0 ? (
                                        <span className="font-bold text-ink-900 tabular-nums">
                                          ${p.total.toLocaleString()}
                                        </span>
                                      ) : (
                                        <span className="text-ink-400">—</span>
                                      )}
                                    </span>
                                  </div>
                                </div>
                                <span className="w-5 h-5 flex items-center justify-center text-ink-300 group-hover:text-ink-700 transition-colors">
                                  <i className="ri-arrow-right-s-line"></i>
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center py-16">
                    <span className="w-14 h-14 flex items-center justify-center rounded-full bg-white border border-ink-100 text-ink-400 mb-3">
                      <i className="ri-cursor-line text-xl"></i>
                    </span>
                    <div className="font-display text-sm font-bold text-ink-900">
                      Pick a day
                    </div>
                    <div className="text-xs text-ink-500 mt-1 max-w-[220px]">
                      Click any date in the calendar to see the jobs on the books — tap a project
                      to jump straight to its details.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-ink-100 bg-white flex items-center justify-between gap-2 shrink-0 rounded-b-lg">
            <div className="text-[11px] text-ink-500">
              Showing{" "}
              <span className="font-bold text-ink-900">{visibleProjects.length}</span> of{" "}
              <span className="font-bold text-ink-900">{projects.length}</span> projects
              {statusFilter !== "all" && (
                <>
                  {" "}
                  · filtered by{" "}
                  <span className="font-bold text-ink-900">
                    {statusStyles[statusFilter as ProjectStatus]?.label}
                  </span>
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function StatTile({
  icon,
  iconClass,
  label,
  value,
  sub,
}: {
  icon: string;
  iconClass: string;
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="bg-white border border-ink-100 rounded-md px-3 py-2.5 flex items-center gap-3">
      <span className={`w-9 h-9 flex items-center justify-center rounded-md ${iconClass}`}>
        <i className={`${icon} text-base`}></i>
      </span>
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500 truncate">
          {label}
        </div>
        <div className="font-display text-base font-bold text-ink-900 leading-tight">
          {value}
        </div>
        <div className="text-[10px] text-ink-500 truncate">{sub}</div>
      </div>
    </div>
  );
}